<template>
  <main class="ai-page">
    <header class="ai-header">
      <button
        class="ai-icon-button"
        aria-label="返回设备页面"
        @click="$router.replace(returnPath($route.query.from))"
      >
        <ai-icon name="back" />
      </button>
      <div class="ai-brand">
        <span class="ai-mini-orb"><ai-icon name="spark" /></span
        ><span><span class="ai-brand-title"><ai-name /><span v-if="pageState.copy.versionBadge" class="ai-version-badge">{{ pageState.copy.versionBadge }}</span></span><small>让空间，听懂你的话</small></span>
      </div>
      <div class="ai-header-actions">
        <button
          class="ai-icon-button"
          aria-label="新建对话"
          :disabled="!canEnterConversation"
          @click="newConversation"
        >
          <ai-icon name="plus" />
        </button>
      </div>
    </header>
    <div class="ai-layout">
      <ai-navigation :settings-open="settingsOpen" @settings="openSettings" @close-settings="settingsOpen = false" :busy="busy" :disabled="!initialized || !caps.enabled || !!initialBlock" :history-open="showHistory" :open="quotaOpen" :loading="quotaLoading" :quota="quota" :overview="quotaOverview" :quota-disabled="!initialized || (!quotaOverviewSupported && (!caps.enabled || !!initialBlock))" :summary="quotaSummary" :copy="pageState.copy" :error="quotaError" @history="toggleHistory" @quota="openQuota" @refresh="loadQuota" @close="quotaOpen = false" />
      <button v-if="showHistory" class="ai-history-backdrop" aria-label="关闭历史对话" @click="toggleHistory" />
      <aside v-if="showHistory" ref="sessionList" @scroll.passive="loadSessionsNearBottom" class="ai-history" tabindex="-1" @keydown.esc="toggleHistory">
        <div class="ai-history-heading"><h2>最近对话</h2></div>
        <ai-notice v-if="sessionsLoading && !sessionList.length" message="正在加载对话…" />
        <ai-notice v-else-if="sessionsError" :message="sessionsError" notice-role="alert"><template #actions><button class="ai-quiet-button" @click="loadSessions(!!sessionsCursor)">重试</button></template></ai-notice>
        <ai-notice v-else-if="!sessionList.length" message="还没有历史对话" />
        <div
          v-for="item in sessionList"
          :key="item.sessionId"
          class="ai-history-item"
          :class="{ 'is-current': session && session.sessionId === item.sessionId }"
        >
          <button :disabled="!canEnterConversation" :aria-current="session && session.sessionId === item.sessionId ? 'true' : null" @click="openHistory(item)">
            <span class="ai-history-title" :title="item.title">{{ item.title || '新对话' }}</span><small>{{
              item.createdAtText || ''
            }}</small></button
          ><button
            :disabled="busy"
            aria-label="删除这段对话"
            @click="removeSession(item)"
          >
            ×
          </button>
        </div>
        <ai-notice v-if="sessionsLoading && sessionList.length" message="正在加载更多对话…" />
        <button
          v-if="sessionsCursor"
          class="ai-quiet-button"
          :disabled="busy"
          @click="loadSessions(true)"
        >
          更多对话
        </button>
      </aside>
      <section ref="main" class="ai-main" :class="{ 'ai-main-welcome': !turns.length }">
        <div
          ref="scroll"
          class="ai-scroll"
          :class="{ 'ai-scroll-restoring': restoringScroll }"
          @scroll.passive="updateScrollPosition"
          aria-live="polite"
          aria-atomic="false"
        >
          <div v-if="turns.length && (historyCursor || historyPaged)" class="ai-history-loader" role="status">
            <button v-if="historyCursor" class="ai-quiet-button" :disabled="historyLoading" @click="moreHistory">{{ historyLoading ? '正在加载…' : historyError ? '加载失败，点击重试' : '加载更早消息' }}</button>
            <span v-else>已加载全部消息</span>
          </div>
          <ai-access-state v-if="initialBlock" :block="initialBlock" :checking="initializing" @retry="bootstrap" />
          <section v-else-if="!turns.length && !pendingRequest" class="ai-welcome">
            <div class="ai-orb"><ai-icon name="spark" /></div>
            <span class="ai-eyebrow">你的专属Ai助理</span>
            <h1>{{ pageState.copy.welcomeTitle }}</h1>
            <p>{{ pageState.copy.welcomeDescription }}</p>
            <div class="ai-suggestions">
              <button v-for="(suggestion, index) in pageState.copy.suggestions || []" :key="index" :disabled="!initialized || !caps.enabled || !!initialBlock" @click="useSuggestion(suggestion.text)">
                <ai-icon name="spark" /><strong>{{ suggestion.label }}</strong><small>{{ suggestion.text }}</small><span>↗</span>
              </button>
            </div>
            <div class="ai-scope">
              <ai-icon name="shield" /><span
                >{{ pageState.copy.scope }}</span
              >
            </div>
          </section>
          <section v-for="turn in turns" :key="turn.turnId" class="ai-turn">
            <div v-if="turn.inputText" class="ai-user-message" @mouseenter="now = Date.now()">
              <p class="ai-user-bubble">{{ turn.inputText }}</p>
              <div class="ai-user-tools"><time v-if="turn.createdAt" class="ai-message-time" :datetime="turn.createdAt" :title="messageTime(turn.createdAt, now).full">{{ messageTime(turn.createdAt, now).label }}</time>
              <button class="ai-copy" aria-label="复制这条消息" title="复制消息" @click="copyText(turn.inputText, turn.turnId + ':user')"><ai-icon :name="copiedKey === turn.turnId + ':user' ? 'check' : 'copy'" /></button></div>
            </div>
            <div class="ai-answer-label">
              <span class="ai-mini-orb"><ai-icon name="spark" /></span>Ai助理<span
                v-if="turn.readOnly"
                class="ai-history-label"
                >历史记录</span
              >
            </div>
            <div class="ai-answer" @mouseenter="now = Date.now()">
              <ai-card
                v-for="card in turn.components"
                :key="card.id"
                :card="card"
                :blocked="turn.blocked"
                :busy="busy"
                :action-busy="activeActionKey === cardRequestKey(turn, card)"
                :pagination="pagination[cardRequestKey(turn, card)]"
                :read-only="turn.readOnly"
                :now="now"
                @action="submitAction(turn, $event)"
                @page="nextPage(turn, $event)"
              />
              <div class="ai-feedback">
                <button class="ai-copy" aria-label="复制Ai助理回复" title="复制回复" @click="copyAnswer(turn)"><ai-icon :name="copiedKey === turn.turnId + ':assistant' ? 'check' : 'copy'" /></button>
                <template v-if="terminal(turn) || turn.state === 'WAITING_ACTION'">
                <button title="有帮助" aria-label="有帮助" :aria-pressed="ratingOf(turn) === 'LIKE'" :disabled="isFeedbackBusy(turn)" @click="toggleFeedback(turn, 'LIKE')"><ai-icon name="like" /></button>
                <button title="需要改进" aria-label="没有帮助" :aria-pressed="ratingOf(turn) === 'DISLIKE'" :disabled="isFeedbackBusy(turn)" @click="toggleFeedback(turn, 'DISLIKE')"><ai-icon name="dislike" /></button>
                <time v-if="turn.finishedAt" class="ai-message-time" :datetime="turn.finishedAt" :title="messageTime(turn.finishedAt, now).full">{{ messageTime(turn.finishedAt, now).label }}</time>
                <ai-notice v-if="ratingOf(turn)" :message="feedbackReceipt(turn)" />
                </template>
              </div>
              <section v-if="feedbackOpen === turn.turnId" ref="feedbackPanel" class="ai-feedback-panel" aria-label="补充反馈">
                <div class="ai-feedback-heading"><strong>{{ feedbackTitle }}</strong><button aria-label="关闭反馈面板" @click="feedbackOpen = null">×</button></div>
                <p class="ai-feedback-prompt">{{ feedbackCopy.prompt }}</p>
                <div class="ai-feedback-reasons">
                  <button v-for="reason in feedbackReasons" :key="reason.code" :disabled="feedbackBusy" :aria-pressed="feedbackReason.includes(reason.code)" @click="feedbackReason = feedbackReason.includes(reason.code) ? feedbackReason.filter(code => code !== reason.code) : feedbackReason.concat(reason.code)">{{ reason.label }}</button>
                </div>
                <textarea v-model="feedbackComment" maxlength="500" rows="2" aria-label="反馈补充说明" :placeholder="feedbackCopy.placeholder" :disabled="feedbackBusy" />
                <ai-notice v-if="feedbackError" :message="feedbackError" notice-role="alert" />
                <div class="ai-feedback-actions"><button @click="feedbackOpen = null">{{ feedbackCopy.skip }}</button><button class="ai-feedback-submit" :disabled="feedbackBusy || !hasFeedbackDetail" @click="feedback(turn, 'DISLIKE', feedbackReason, feedbackComment, true)">{{ feedbackBusy ? feedbackCopy.saving : feedbackCopy.submit }}</button></div>
              </section>
            </div>
          </section>

          <section v-if="pendingRequest" class="ai-turn" aria-live="polite">
            <div class="ai-user-message"><p class="ai-user-bubble">{{ pendingRequest.text }}</p></div>
            <template v-if="sending">
              <div class="ai-answer-label"><span class="ai-mini-orb"><ai-icon name="spark" /></span>Ai助理</div>
              <div class="ai-answer"><ai-execution-state status="NOT_STARTED" :progress="true" label="正在处理，请稍候" /></div>
            </template>
            <p v-else class="ai-submission-status" role="status">发送结果待确认，请重试原请求</p>
          </section>
        </div>
        <footer v-show="!initialBlock" ref="composerWrap" class="ai-composer-wrap" :class="{ 'has-composer-notices': !!(composerError || historyMode || copyNotice || quotaNotice || speechNotice) }">
          <button v-if="turns.length && !nearBottom" class="ai-jump" aria-label="回到最新消息" title="回到最新消息" @click="scrollToLatest"><ai-icon name="arrow-down" /></button>
          <ai-notice v-if="composerError" :message="composerError" notice-role="alert"><template #actions>
            <button v-for="action in errorActions" :key="action.id" :disabled="busy || speechActive || initializing" @click="action.id === 'retry' ? retry() : newConversation()">{{ action.label }}</button>
          </template></ai-notice>
          <div v-if="initialized && caps.enabled && !consented" class="ai-consent">
            <div class="ai-consent-scroll" tabindex="0" role="region" aria-label="使用说明"><ai-notice :message="pageState.copy.consent" /></div>
            <button class="ai-primary" @click="acceptConsent">
              同意并开始
            </button>
          </div>
          <template v-else>
            <ai-notice v-if="historyMode" :message="historyNotice()"><template #actions><button
                class="ai-quiet-button ai-new-conversation-link"
                :disabled="!canEnterConversation"
                @click="newConversation"
              >
                新建对话
              </button>
            </template></ai-notice>
            <ai-notice v-if="copyNotice" :message="copyNotice" />
            <ai-notice v-if="quotaNotice" :message="quotaNotice.text"><template #actions><button @click="openQuota">{{ quotaNotice.actionLabel }}</button></template></ai-notice>
            <ai-notice v-if="speechNotice" class="ai-speech-notice" :message="speechNotice" />
            <form class="ai-composer" :class="{ 'is-speech-active': speechActive }" @submit.prevent="send">
              <textarea
                ref="composer"
                :aria-hidden="speechActive ? 'true' : 'false'"
                v-model="draft"
                aria-label="告诉Ai助理你想做什么"
                :placeholder="latest && latest.state === 'WAITING_ACTION' ? pageState.copy.waitingPlaceholder : pageState.copy.placeholder"
                rows="1"
                maxlength="2000"
                :disabled="
                  speechActive || !initialized || !caps.enabled ||
                    busy ||
                    pendingRequest !== null ||
                    historyMode
                "
                @keydown.enter.exact.prevent="onEnter"
              />
              <speech-input :disabled="!initialized || !caps.enabled || busy || pendingRequest !== null || historyMode || canStop" @active="speechActive = $event" @capture="captureSpeechCursor" @recognized="insertSpeech" @notice="onSpeechNotice" />
              <button v-if="canStop" type="button" class="ai-send" :disabled="busy" :aria-label="composerLabel" :title="composerLabel" @click="cancel"><span class="ai-stop-symbol" /></button>
              <button v-else type="submit" class="ai-send" :disabled="speechActive || !canSend" :aria-label="composerLabel" :title="composerLabel"><ai-icon name="send" /></button>
            </form>
            <div v-if="initialized && !caps.modelEnabled" class="ai-composer-meta" role="status">
              <span>当前 AI 服务暂不可用，请稍后重试。</span>
              <button type="button" :disabled="initializing || busy" @click="bootstrap">刷新服务状态</button>
            </div>

            <ai-notice v-if="!caps.controlEnabled" message="当前设备控制尚未开放。" />
          </template>
        </footer>
      </section>
    </div>
    <confirm-dialog :visible="!!deleteDialog" title="删除对话" :message="deleteDialog ? deleteDialog.message : ''" confirm-text="删除" @confirm="resolveDelete(true)" @cancel="resolveDelete(false)" />
  </main>
</template>
<script>
import historyMethods from './state/conversation-history'
import progressMethods from './state/turn-progress'
import actionMethods from './state/turn-actions'
import feedbackMethods from './state/feedback-methods'
import quotaMethods from './state/quota-methods'
import { availabilityError, availabilityRestriction, composerError, errorActions } from './state/availability-state'
import AiAccessState from './components/AiAccessState.vue'
import { canEnterConversation } from './state/conversation-entry-policy'
import ConfirmDialog from '@/components/common/confirm-dialog/confirm-dialog.vue'
import SpeechInput from '@/speech/SpeechInput.vue'
import { createSpeechAdoptionReporter } from '@/integration/speech-ai'
import { hasConsent, saveConsent, returnPath, initializeTabSession, voiceDirectSend } from '@/ai/state/preferences'
import { messageTime } from '@/ai/presentation/message-time'
import { quotaWarning } from '@/ai/presentation/quota'
import AiNotice from './components/AiCompatibilityNotice.vue'
import AiName from '@/components/content/brand/AiName.vue'
import AiIcon from '@/components/common/icons/AiIcon.vue'
import AiCard from './components/AiCard.vue'
import AiExecutionState from './components/AiExecutionState.vue'
import AiNavigation from './components/AiNavigation.vue'
import { installAiGestureGuard } from '@/ai/presentation/gesture-guard'
import { CAPABILITIES } from '@/ai/protocol/contract';
import {
  capabilities,
  createAiClient,
  errorMessage,
  identity,
  requestId
} from '@/ai/services/api'

import { feedbackCopy } from '@/ai/presentation/feedback-copy'; // 所有新消息共用准入条件，待发送文字不依赖输入框状态。

// 所有新消息共用准入条件，待发送文字不依赖输入框状态。
function canSubmitText(page, text) {
  return (
    page.caps.enabled && page.caps.modelEnabled &&
    page.initialized && page.consented &&
    !page.availabilityBlock && !page.busy && !page.historyMode && !page.pendingRequest &&
    typeof text === 'string' && text.trim() && text.length <= 2000 &&
    (!page.latest || page.terminal(page.latest) || page.latest.state === 'WAITING_ACTION')
  )
}

export default {
  name: 'AiPage',
  components: { AiAccessState, ConfirmDialog, SpeechInput, AiName, AiIcon, AiCard, AiExecutionState, AiNavigation, AiNotice },
  provide() { return { aiPageState: this.pageState } },
  data() {
    return {
      caps: capabilities(),
      pageState: { copy: {}, valid: true },
      initialized: false,
      initializing: false,
      restoringScroll: false,
      sessionsLoaded: false,
      draft: '', speechActive: false, speechNotice: '', speechRefs: [], speechMixed: false,
      turns: [],
      session: null,
      sessionList: [],
      deleteDialog: null,
      sessionsLoading: false,
      sessionsError: '',
      sessionsCursor: null,
      showHistory: false,
      historyMode: false,
      historyCursor: null,
      historyPaged: false,
      historyLoading: false,
      historyError: false,
      sending: false,
      stopping: false,
      actionSubmitting: false,
      paging: false,
      sessionChanging: false,
      activeActionKey: null,
      pagination: {},
      nearBottom: true,
      error: '',
      errorCode: '',
      initialBlock: null,
      availabilityBlock: null,
      copyNotice: '',
      copiedKey: '',
      quota: null,
      quotaLoading: false,
      quotaOpen: false,
      quotaOverview: null,
      quotaOverviewSupported: false,
      settingsOpen: false,
      quotaSummary: null,
      quotaError: '',
      consented: false,
      pendingRequest: null,
      cardActionTurnId: null,
      sessionRequest: null,
      ratings: {},
      feedbackDetails: {},
      feedbackOpen: null,
      feedbackCopy,
      feedbackTitle: '',
      feedbackReason: [],
      feedbackComment: '',
      feedbackPending: {},
      feedbackError: '',
      feedbackReasons: [
        { code: 'NOT_UNDERSTOOD', label: '没理解我的意思' },
        { code: 'WRONG_RESULT', label: '结果不准确' },
        { code: 'TOO_SLOW', label: '响应太慢' }
      ],
      now: Date.now()
    }
  },
  computed: {
    composerError() { return composerError(this) },
    errorActions() { return errorActions(this) },
    canEnterConversation() { return canEnterConversation(this) },
    feedbackBusy() {
      return !!this.feedbackPending[this.feedbackOpen]
    },
    quotaNotice() { return quotaWarning(this.quotaSummary) },
    hasFeedbackDetail() { return !!(this.feedbackReason.length || this.feedbackComment.trim()) },
    latest() {
      return this.turns[this.turns.length - 1]
    },
    cardOwnsProgress() {
      return !!(this.latest && ((this.cardActionTurnId && this.cardActionTurnId === this.latest.turnId) || (this.latest.components || []).some(card => card.type === 'action_list')))
    },
    // Keep the shared interaction lock, but never use it to choose a progress icon.
    // Only message/cards own progress; unrelated requests must not change stop into send.
    busy() {
      return !!(this.sending || this.stopping || this.actionSubmitting || this.paging || this.sessionChanging)
    },
    canStop() {
      return !this.pendingRequest && !this.cardOwnsProgress && !this.historyMode && !!(this.latest && !this.latest.readOnly && this.latest.canCancel === true && ['QUEUED', 'GENERATING'].includes(this.latest.state))
    },
    composerLabel() {
      if (this.sending) return '正在提交消息'
      if (this.stopping) return '正在请求停止'
      if (this.pendingRequest) return '请求结果尚未确认，请重试原请求'
      if (this.canStop) return this.busy ? '请等待当前操作完成后停止' : '停止处理'
      if (this.historyMode) return '新建对话后可发送消息'
      if (this.busy) return '请等待当前操作完成'
      if (this.latest && !this.latest.readOnly && !this.terminal(this.latest) && this.latest.state !== 'WAITING_ACTION') return '正在处理，请在消息中查看进度'
      return '发送消息'
    },
    canSend() {
      return canSubmitText(this, this.draft)
    }
  },
  watch: {
    quotaOpen(open) {
      if (!open) { clearTimeout(this._quotaRefreshTimer); this._quotaRefreshPending = false }
    },
    '$store.state.token'(token) {
      if (!token) {
        this.stop()
        this.draft = ''; this.speechRefs = []; this.speechNotice = ''
        this.pendingSpeech = null; this.pendingRequest = null
      }
    },
    draft() { this.$nextTick(this.resizeComposer) }
  },
  updated() {
    if (!this._composerResize) this.measureComposer()
  },
  mounted() {
    this._api = createAiClient()
    this._quotaApi = createAiClient('/usage/v1')
    this._identity = identity()
    this._alive = true
    this._removeGestureGuard = installAiGestureGuard(this.$el)
    this._visibility = () => {
      clearTimeout(this._poll)
      if (document.hidden) { clearTimeout(this._expiryTimer); return }
      if (!this._alive) return
      this.armExpiry()
      if (this.shouldPoll(this.latest)) this.refresh(this.latest)

    }
    document.addEventListener('visibilitychange', this._visibility)
    this.$nextTick(() => this.measureComposer())
    if (typeof ResizeObserver !== 'undefined') {
      this._composerResize = new ResizeObserver(() => this.measureComposer())
      this._composerResize.observe(this.$refs.composerWrap)
    } else window.addEventListener('resize', this.measureComposer)
    this.bootstrap()
  },
  beforeDestroy() {
    this.stop()
    if (this._removeGestureGuard) this._removeGestureGuard()
    if (this._composerResize) this._composerResize.disconnect()
    window.removeEventListener('resize', this.measureComposer)
    document.removeEventListener('visibilitychange', this._visibility)
  },
  methods: {
    returnPath,
    acceptConsent() {
      if (this.consented) return
      saveConsent(this.caps.consentVersion)
      this.consented = true
    },
    messageTime,
    measureComposer() {
      if (!this._alive || !this.$refs.main || !this.$refs.composerWrap) return
      const height = Math.ceil(this.$refs.composerWrap.getBoundingClientRect().height)
      if (height === this._composerHeight) return
      this._composerHeight = height
      this.$refs.main.style.setProperty('--ai-composer-clearance', (height + 24) + 'px')
    },
    updateScrollPosition() {
      const box = this.$refs.scroll
      this.nearBottom = !box || box.scrollHeight - box.scrollTop - box.clientHeight < 80
      if (box && box.scrollTop < 60 && !this.restoringScroll && this.historyCursor && !this.historyError) this.moreHistory()
    },
    scrollToLatest() {
      const box = this.$refs.scroll
      if (box) box.scrollTop = box.scrollHeight
      this.nearBottom = true
    },
    resizeComposer() {
      const input = this.$refs.composer
      if (!input) return
      input.style.height = 'auto'
      input.style.height = Math.min(input.scrollHeight, 116) + 'px'
    },
    ...historyMethods,
    copyAnswer(turn) {
      const lines = []
      for (const card of turn.components) {
        if (card.text) lines.push(card.text)
        if (card.title) lines.push(card.title)
        if (card.description) lines.push(card.description)
        if (card.targetLabel) lines.push([card.operationLabel, card.targetLabel].filter(Boolean).join(' · '))
        for (const item of card.items || []) lines.push(card.type === 'info_list' ? item.label + '：' + item.value : [item.subtitle, item.title, item.statusText].filter(Boolean).join(' · '))
        for (const item of card.entries || card.parameters || []) lines.push(item.label + '：' + item.value)
        if (card.impact) lines.push(card.impact)
      }
      return this.copyText(lines.join('\n'), turn.turnId + ':assistant')
    },
    async copyText(value, key) {
      if (!value) return
      let copied = false
      if (navigator.clipboard && window.isSecureContext) {
        try { await navigator.clipboard.writeText(value); copied = true } catch (_) { /* 继续使用用户手势复制降级。 */ }
      }
      if (!copied) {
        const active = document.activeElement
        const area = document.createElement('textarea')
        area.value = value
        area.style.cssText = 'position:fixed;left:-9999px;top:0;user-select:text'
        document.body.appendChild(area)
        try { area.focus(); area.select(); copied = document.execCommand('copy') } catch (_) { copied = false }
        finally { area.remove(); if (active && active.focus) active.focus() }
      }
      this.copiedKey = copied ? key : ''
      this.copyNotice = copied ? '已复制' : '复制未成功，请选中文字复制。'
      clearTimeout(this._copyTimer)
      this._copyTimer = setTimeout(() => { this.copiedKey = ''; this.copyNotice = '' }, 1800)
    },

    async bootstrap({ availabilityOnly = false } = {}) {
      if (this.initializing) return
      this.initializing = true
      const owner = this._api, epoch = this._conversationEpoch || 0, previousBlock = this.availabilityBlock, previousError = this.error
      try {
        initializeTabSession()
        const sessionId = availabilityOnly ? null : sessionStorage.getItem(this.resumeKey())
        const result = await this.call('page/bootstrap', sessionId ? { sessionId, capabilities: CAPABILITIES } : {})
        if (!this._alive || owner !== this._api || epoch !== (this._conversationEpoch || 0) ||
          previousBlock !== this.availabilityBlock) return false
        const restriction = availabilityRestriction(result.availability)
        if (!result.capabilities || typeof result.capabilities.enabled !== 'boolean' ||
          !restriction && !result.capabilities.enabled) throw new Error('WRONG_RESPONSE')
        const consented = hasConsent(result.capabilities.consentVersion)
        this.availabilityBlock = restriction
        this.initialBlock = !this.turns.length ? restriction : null
        this.quotaOverviewSupported = result.quotaOverviewSupported === true
        this.caps = result.capabilities
        this.consented = consented
        this.pageState.copy = availabilityOnly && this.pageState.copy.suggestions
          ? { ...(result.copy || {}), suggestions: this.pageState.copy.suggestions }
          : result.copy || {}
        this.initialized = true
        if (!availabilityOnly || this.error === previousError) {
          this.errorCode = restriction && restriction.reason || ''
          this.error = (restriction && this.turns.length ? restriction.message : '') || result.resumeError || ''
        }
        if (availabilityOnly) {
          if (!restriction && this.pendingRequest && !this.error) this.error = '发送结果待确认，请重试原请求'
          return !restriction
        }
        if (result.session) {
          this.restoringScroll = true
          this.session = result.session
          this.historyCursor = result.session.nextCursor || null
          this.historyPaged = false
          this.historyError = false
          this.consented = hasConsent(this.caps.consentVersion)
          for (const turn of result.session.items) this.apply(turn)
          // Position after message/composer layout, before revealing restored content.
          await this.$nextTick()
          this.measureComposer()
          await this.$nextTick()
          this.scrollToLatest()
        } else if (sessionId && !restriction) sessionStorage.removeItem(this.resumeKey())
      } catch (error) {
        if (!this._alive || owner !== this._api || epoch !== (this._conversationEpoch || 0)) return false
        if (availabilityOnly && !['TOKEN_ERROR', 'ACCOUNT_CHANGED'].includes(error.message)) {
          if (!error.silent) this.$toast('刷新失败，请重试', 1500)
          return false
        }
        this.fail(error)
        if (this._alive && !error.silent && !this.turns.length) this.initialBlock = { title: '暂时无法连接 AI 服务', message: errorMessage(error) }
      }
      finally { this.restoringScroll = false; this.initializing = false }
    },
    stop() {
      this.resolveDelete(false)
      this.settingsOpen = false
      this._alive = false
      if (this._speechAdoption) this._speechAdoption.close()
      this.pageState.valid = false
      this.quotaOpen = false
      this.quota = null
      this.quotaOverview = null
      this.quotaSummary = null
      clearTimeout(this._quotaRefreshTimer)
      this._quotaRefreshPending = false
      clearTimeout(this._poll)
      clearTimeout(this._expiryTimer)
      this._pollEpoch = (this._pollEpoch || 0) + 1
      clearTimeout(this._copyTimer)
      if (this._api) this._api.close()
      if (this._quotaApi) this._quotaApi.close()
    },
    ...progressMethods,
    async call(path, body) {
      const epoch = this._conversationEpoch || 0
      let result
      const changed = () => { const error = new Error('AI_CONVERSATION_CHANGED'); error.silent = true; return error }
      try { result = await this._api.post(path, body) }
      catch (error) { if (epoch !== (this._conversationEpoch || 0)) throw changed(); throw error }
      if (epoch !== (this._conversationEpoch || 0)) throw changed()
      if (!this._alive) throw new Error('AI_PAGE_CLOSED')
      if (Object.prototype.hasOwnProperty.call(result, 'quotaSummary')) this.adoptQuotaSummary(result.quotaSummary, path === 'quota/get')
      return result
    },
    fail(error) {
      if (error.message === 'AI_CONVERSATION_CHANGED') return
      if (this._alive) {
        if (error.message === 'TOKEN_ERROR' || error.message === 'ACCOUNT_CHANGED') this.stop()
        this.errorCode = error.message || ''
        this.error = errorMessage(error)
        if (availabilityError(this.errorCode)) this.availabilityBlock = { reason: this.errorCode, message: this.error }

      }
    },
    onEnter(event) {
      if (!event.isComposing && event.keyCode !== 229) this.send()
    },
    useSuggestion(text) {
      if (this.busy || !this.initialized || !this.caps.enabled) return
      if (!this.consented) {
        this.$toast('请先同意使用说明，再使用 Ai助理', 3500)
        return
      }
      this.send(text)
    },
    onSpeechNotice(message, popup = false) {
      this.speechNotice = popup ? '' : message
      if (popup && message) this.$toast(message, 3500)
    },
    captureSpeechCursor() {
      const input = this.$refs.composer
      this.speechSelection = { start: input.selectionStart, end: input.selectionEnd }
    },
    insertSpeech(result) {
      if (!result.text || !result.text.trim()) return
      const hadDraft = this.draft.length > 0 || this.speechRefs.length > 0
      const directSend = voiceDirectSend()
      const selection = this.speechSelection || { start: this.draft.length, end: this.draft.length }
      const next = this.draft.slice(0, selection.start) + result.text + this.draft.slice(selection.end)
      if (next.length > 2000) { this.onSpeechNotice('识别文字超过当前输入上限，请缩短录音后重试。', true); return }
      if (this.speechRefs.length >= 10) { this.onSpeechNotice('一条消息最多关联10段录音，请先发送当前文字。', true); return }
      const fillDraft = () => {
        this.speechMixed = this.speechMixed || hadDraft
        this.speechRefs.push(result.recognitionId); this.draft = next
        if (directSend && hadDraft) this.onSpeechNotice('已合并到草稿，请确认后发送', true)
        this.$nextTick(() => {
          const input = this.$refs.composer
          if (input) { input.focus(); input.setSelectionRange(selection.start + result.text.length, selection.start + result.text.length) }
        })
      }
      if (!directSend || hadDraft) { fillDraft(); return }
      // recognized 先于录音组件 finally 清理；等 active 事件传播后直接提交，不回填输入框。
      this.$nextTick(async () => {
        await this.$nextTick()
        if (!this._alive) return
        if (canSubmitText(this, result.text) && !this.speechActive) {
          this.send(result.text, { recognitionIds: [result.recognitionId], singleVoice: true })
        } else {
          fillDraft()
        }
      })
    },
    async send(text, speech = null) {
      const direct = typeof text === 'string'
      if (this.speechActive || this.availabilityBlock) return
      if ((direct ? !canSubmitText(this, text) : (!this.canSend && !this.pendingRequest)) || this.busy || !this._alive)
        return
      const submittedText = direct ? text.trim() : this.draft.trim()
      this.sending = true
      this.error = ''
      this._pollFailures = 0
      this._pollDelay = 1000
      this._pollDeadline = null
      try {
        if (!this.session && !this.sessionRequest) this.sessionRequest = {
          clientRequestId: requestId(), clientType: 'WEB', protocolMajor: 1, protocolMinor: 0,
          capabilities: CAPABILITIES, consentVersion: this.caps.consentVersion
        }
        if (!this.pendingRequest)
          this.pendingRequest = {
            ...(this.session ? { sessionId: this.session.sessionId } : { sessionRequest: this.sessionRequest }),
            clientRequestId: requestId(),
            text: submittedText,
            ...(this.latest && this.latest.state === 'WAITING_ACTION' ? { replyToTurnId: this.latest.turnId, expectedTurnRevision: this.latest.revision } : {})
          }
        this.$nextTick(this.scrollToLatest)
        if (!this.pendingSpeech) this.pendingSpeech = direct
          ? (speech || { recognitionIds: [], singleVoice: false })
          : { recognitionIds: (this.speechRefs || []).slice(), singleVoice: !this.speechMixed }
        const snapshot = await this.call('turn/create', this.pendingRequest)
        const association = { ...this.pendingSpeech, turnId: snapshot.turnId }
        if (!this.session) {
          this.session = { sessionId: snapshot.sessionId, state: 'OPEN' }
          sessionStorage.setItem(this.resumeKey(), snapshot.sessionId)
          this.sessionsLoaded = false
        }
        const previousId = this.pendingRequest.replyToTurnId
        if (previousId) {
          const previous = this.turns.find(t => t.turnId === previousId)
          if (previous) this.$set(this.turns, this.turns.indexOf(previous), { ...previous, readOnly: true })
        }
        this.apply(snapshot)
        this.$nextTick(this.scrollToLatest)
        this.pendingRequest = null; this.pendingSpeech = null
        this.draft = ''; this.speechRefs = []; this.speechMixed = false
        if (association.recognitionIds.length) {
          if (!this._speechAdoption) this._speechAdoption = createSpeechAdoptionReporter()
          // Result rendering/polling already started; statistics cannot hold the composer busy.
          this._speechAdoption.report(association)
        }
      } catch (error) {
        if (direct && this._alive && !['TOKEN_ERROR', 'ACCOUNT_CHANGED'].includes(error.message)) {
          this.draft = submittedText
          this.speechRefs = speech ? speech.recognitionIds.slice() : []
          this.speechMixed = speech ? !speech.singleVoice : false
        }
        if (error.definitive) { this.pendingRequest = null; this.pendingSpeech = null }
        this.fail(error)
      } finally {
        this.sending = false
        this.$nextTick(this.updateScrollPosition)
      }
    },

    ...actionMethods,

    ...feedbackMethods,

    ...quotaMethods,

    openSettings() {
      this.quotaOpen = false
      this.showHistory = false
      this.settingsOpen = !this.settingsOpen
    },

    async retry() {
      if (!this._alive || this.busy || this.speechActive || this.initializing || this._retrying) return false
      this._retrying = true
      const epoch = this._conversationEpoch || 0
      const previousError = this.error, previousCode = this.errorCode;
      const hasContext = !!(this.session || this.sessionRequest);
      try {
        if (this.availabilityBlock) {
          const recovered = await this.bootstrap({ availabilityOnly: true })
          if (recovered && this._alive && epoch === (this._conversationEpoch || 0))
            this.$toast(this.pendingRequest ? '连接已恢复，请重试原请求' : '连接已恢复，可以继续对话', 1000)
          return recovered === true
        }
        if (this.initialized && this.latest && !this.pendingRequest)
          return await this.refresh(this.latest, true)
        if (this.initialized && this.pendingRequest) await this.send()
        else await this.bootstrap()
        if (!this._alive || epoch !== (this._conversationEpoch || 0)) return false
        if (!this.initialized || this.initialBlock || this.error) {
          this.$toast('重试未成功，请重试或新建对话', 1500)
          return false
        }
        if (hasContext && !this.latest && !this.pendingRequest) {
          // Keep the recovery entry: a successful capability read is not a
          // successful close or turn read. Never silently hide its old error.
          this.error = previousError
          this.errorCode = previousCode
          this.$toast('连接正常，请新建对话后继续', 1000)
          return false
        }
        this.$toast(this.latest ? '原请求已提交' : '连接已恢复，可以继续对话', 1000)
        return true
      } catch (error) {
        if (this._alive && epoch === (this._conversationEpoch || 0) && !error.silent) {
          this.fail(error);
          if (!this.error) { this.error = previousError; this.errorCode = previousCode; }
          this.$toast('重试未成功，请重试或新建对话', 1500);
        }
        return false;
      } finally {
        this._retrying = false
      }
    },

  }
}
</script>
<style scoped src="./page.css"></style>
